import React, { createContext, useContext, useLayoutEffect, useEffect, useRef, useState, useCallback } from 'react';

const ThemeContext = createContext({
  theme: 'dark',
  toggleTheme: () => {},
  setTheme: () => {},
  setBrandScope: () => {},
});

function applyTheme(theme) {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
}

function initialTheme(key, fallback) {
  // ?theme=light|dark wins (shareable links / quick preview), then saved pref.
  try {
    const q = new URLSearchParams(window.location.search).get('theme');
    if (q === 'light' || q === 'dark') return q;
  } catch (_) { /* ignore */ }
  let saved = null;
  try { saved = localStorage.getItem(key); } catch (_) { /* ignore */ }
  return saved === 'light' || saved === 'dark' ? saved : fallback;
}

function persist(key, value) {
  try { localStorage.setItem(key, value); } catch (_) { /* ignore */ }
}

/**
 * Two remembered themes: the console's (dark by default) and the auth pages' (light
 * paper by default). Brand pages switch scope with setBrandScope (see useBrandTheme):
 * 'pref' follows the remembered brand theme, 'light' pins the paper palette (marketing
 * pages and docs, which have no toggle), false is the console. The toggle on a brand
 * page never changes the console's theme, and vice versa.
 */
export function ThemeProvider({ children }) {
  const [appTheme, setAppTheme] = useState(() => initialTheme('theme', 'dark'));
  const [brandTheme, setBrandTheme] = useState(() => initialTheme('brand_theme', 'light'));
  // index.html marks brand routes (data-brand="light" | "pref") before React loads;
  // starting in that scope avoids painting the console theme for a frame.
  const [brandScope, setBrandScopeState] = useState(
    () => (typeof document !== 'undefined' && document.documentElement.dataset.brand) || false,
  );
  const theme = brandScope === 'light' ? 'light' : brandScope ? brandTheme : appTheme;

  // That hint is only a guess from the URL: a signed-in visitor on /login is redirected
  // before any brand page mounts, and a path like /docs-old renders the 404. So the hint
  // holds only if a brand page claims the scope in the first commit (children's layout
  // effects run before this one); otherwise the console theme takes over before paint.
  const claimed = useRef(false);
  const setBrandScope = useCallback((scope) => {
    if (scope) claimed.current = true;
    setBrandScopeState(scope);
  }, []);
  useLayoutEffect(() => {
    delete document.documentElement.dataset.brand;
    if (!claimed.current) setBrandScopeState(false);
  }, []);

  // Layout effects so a scope switch repaints with the right classes, not a frame late.
  // The provider alone owns `lf-theme`, so it can never outlive the scope it stands for.
  useLayoutEffect(() => { applyTheme(theme); }, [theme]);
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('lf-theme', !!brandScope);
  }, [brandScope]);
  useEffect(() => { persist('theme', appTheme); }, [appTheme]);
  useEffect(() => { persist('brand_theme', brandTheme); }, [brandTheme]);

  const setActive = brandScope ? setBrandTheme : setAppTheme;
  const setTheme = useCallback((t) => setActive(t === 'light' ? 'light' : 'dark'), [setActive]);
  const toggleTheme = useCallback(() => setActive((t) => (t === 'dark' ? 'light' : 'dark')), [setActive]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, setBrandScope }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;
